/**
 * Overview — what this deployment is doing right now (redesign phase 4a).
 *
 * The operator's landing screen, and the only one whose job is "is anything
 * wrong". Nothing here is editable: every tile either states a number or
 * deep-links to the screen where the corresponding action lives.
 *
 * ── The headline has to add up ──────────────────────────────────────────
 *
 * `totals.spendBillingMicro` is the union of both metered ledgers, summed server-side
 * from the same rows the top-tenants list is sliced from. That is the whole
 * reason `GET /api/admin/usage` was replaced rather than rendered: it read
 * `agent_usage` alone on a rolling-day window, so a "platform total" placed
 * above per-tenant rows was structurally smaller than their sum AND keyed to a
 * different window. A total that does not reconcile with the rows beneath it
 * destroys trust in every other number on the page.
 *
 * ── FOUR units on one screen, and every one of them is named ───────────
 *
 * This paragraph used to say three, and to call spend "USD microdollars,
 * canonical, what providers bill us". That was true before P6/P7 moved the
 * spend query onto `cost_billing_micro` and false afterwards — and the screen
 * went on formatting those figures with `formatMoney`'s USD default, so an MYR
 * deployment printed ringgit behind a `$`. The unit is part of the value; a
 * field name and a doc comment are how a reader picks a symbol.
 *
 *   1. SPEND — `*BillingMicro`, micro units of `billingCurrency`. What
 *      CUSTOMERS WERE CHARGED, and the same figure their caps are enforced
 *      against. Rendered with `billingMoney` (rate 1 — the amount is already
 *      in that currency, so the display multiplier would convert it twice).
 *   2. GRACE — USD microdollars. Money the platform gave away, measured
 *      against a ceiling whose fallback chain ends in an env var and a $5
 *      constant. No billing-native column exists for it. Rendered with
 *      an explicitly named currency, never `formatMoney`'s default.
 *   3. PROVIDER COST — USD microdollars. Cost of goods; providers invoice in
 *      USD and converting it would produce a number nobody pays.
 *   4. REVENUE — MINOR units of the billing currency. What customers actually
 *      paid, straight from the ledger.
 *
 * The realised rate is the bridge between 3 and 4 — minor units per USD — and
 * it is the number directly comparable to the credit rate the operator SET.
 * Nothing here is ever ADDED across those units, because doing so would need an
 * FX rate this system deliberately does not have.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown } from '@papan/core-ui/ui'
import { formatMoney, formatCurrencyMinor } from '@papan/types'

/**
 * ── EVERY MONEY FIGURE ON THIS SCREEN IS BILLING-CURRENCY MICRO ────────
 *
 * There is no USD constant any more. One existed for the grace tiles, the last
 * spend figure that was still USD while its ceiling resolved in billing micro —
 * two units in one comparison, on the number that says how much the platform is
 * giving away. Gate 1 settled that rung in one unit, so nothing on this page is
 * USD.
 *
 * The currency is still NAMED at every call rather than left to `formatMoney`'s
 * USD fallback: relying on that default is exactly what put a `$` on the
 * billing-native spend tiles in the first place.
 */
import { budgetApi, type OverviewResponse } from '../../client/budget'
import { Section, StatTile } from './bits'

export function PlatformOverview({
  onOpenCustomer,
  onOpenOperations,
  onOpenSettings,
}: {
  onOpenCustomer: (tenantId: string) => void
  /** Every alert tile is a link, not a label — the count is only useful next
   *  to the queue that resolves it. */
  onOpenOperations: () => void
  /** Same rule for the grace ceiling: it is a CONFIGURED number, and a tile that
   *  states one without reaching its control is what left this page telling
   *  operators to edit an environment variable. Optional so a host that has not
   *  wired it renders a plain tile rather than a dead button. */
  onOpenSettings?: () => void
}) {
  const { t } = useTranslation('core-ui')
  const [data, setData] = useState<OverviewResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await budgetApi.getOverview())
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load overview')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // The shelf/settings deck stays MOUNTED behind `display: none`, so the mount
  // effect above is the only fetch this page would ever make — it would serve
  // whatever was true when the deck was first opened, with no error and no way
  // back but a full reload. See `DeckVisibility`.
  useRefreshWhenShown(() => { void load() })

  if (!data) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  const { totals, margin, grace, alerts } = data

  /**
   * The format options every BILLING-NATIVE figure on this screen goes through.
   *
   * `rate: 1` explicitly: the amounts are already in `billingCurrency`, so the
   * display-currency multiplier would be a SECOND conversion — wrong by the
   * whole credit rate and silent, because the result stays a plausible number.
   * `formatMoney`'s own header says a non-1 rate is now a defect.
   *
   * Passing the currency is not cosmetic. `formatMoney` is dependency-free and
   * defaults to `'USD'`, so before this every tile on an MYR deployment printed
   * a `$` in front of ringgit — the operator's landing page asserting a unit the
   * whole system had moved off.
   *
   * GRACE is deliberately NOT formatted with this: it is USD, measured against a
   * USD ceiling, and is rendered with the default below.
   */
  const billingMoney = { currency: data.billingCurrency, rate: 1 }

  return (
    <div>
      <h2 className="themed-text text-lg font-medium mb-1">
        {t('settings.platform.overview.title', { defaultValue: 'Overview' })}
      </h2>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.platform.overview.desc', {
          defaultValue: 'What this deployment is doing this period, across every tenant.',
        })}{' '}
        <span className="themed-text-muted">({data.period})</span>
      </p>

      <AlertRow alerts={alerts} billingEnabled={data.billingEnabled} onOpen={onOpenOperations} />

      <Section
        title={t('settings.platform.overview.spend', { defaultValue: 'Spend this period' })}
        desc={t('settings.platform.overview.spendDesc', {
          defaultValue:
            'Every metered surface on every tenant — LLM plus compute, search and storage. This is what customers were CHARGED, in this deployment\'s billing currency, and it is the sum of the per-tenant figures. What the providers bill us is the margin tile below, in USD.',
        })}
      >
        <div className="flex flex-wrap gap-2">
          <StatTile
            label={t('settings.platform.overview.total', { defaultValue: 'Total' })}
            value={formatMoney(totals.spendBillingMicro, billingMoney)}
          />
          <StatTile
            label={t('settings.budget.feature.name.llm', { defaultValue: 'LLM' })}
            value={formatMoney(totals.llmCostBillingMicro, billingMoney)}
          />
          <StatTile
            label={t('settings.platform.overview.otherFeatures', { defaultValue: 'Compute, search, storage' })}
            value={formatMoney(totals.resourceCostBillingMicro, billingMoney)}
          />
          <StatTile
            label={t('settings.platform.overview.activeTenants', { defaultValue: 'Tenants with spend' })}
            value={totals.activeTenants}
          />
          <StatTile
            label={t('settings.platform.overview.messages', { defaultValue: 'Messages' })}
            value={totals.messages.toLocaleString()}
          />
        </div>
      </Section>

      <Section
        title={t('settings.platform.overview.topTenants', { defaultValue: 'Top spenders' })}
        desc={t('settings.platform.overview.topTenantsDesc', {
          defaultValue: 'The tenants this period\'s bill is mostly made of. Open one to see or change anything about it.',
        })}
      >
        <div className="themed-bg-secondary rounded-lg overflow-hidden">
          {totals.activeTenants === 0 && (
            <div className="px-3 py-4 themed-text-muted text-sm">{t('settings.budget.noData')}</div>
          )}
          {data.topTenants.map((row) => (
            <button
              key={row.tenantId}
              onClick={() => onOpenCustomer(row.tenantId)}
              className="w-full text-left px-3 py-2.5 border-b themed-border last:border-b-0 hover:themed-bg-tertiary transition-colors flex items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="themed-text text-sm truncate">
                  {/* A deleted tenant's spend still counts toward the total —
                      usage rows outlive the tenant — so the row is shown with
                      its id rather than dropped, which would break the sum. */}
                  {row.name ?? t('settings.platform.overview.deletedTenant', { defaultValue: 'Deleted tenant' })}
                </div>
                <div className="text-[11px] themed-text-muted font-mono truncate">{row.tenantId}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="themed-text text-sm tabular-nums">{formatMoney(row.spendBillingMicro, billingMoney)}</div>
                <div className="text-[11px] themed-text-muted tabular-nums">
                  {t('settings.platform.overview.llmOf', {
                    defaultValue: 'LLM {{llm}}',
                    llm: formatMoney(row.llmCostBillingMicro, billingMoney),
                  })}
                </div>
              </div>
              <span aria-hidden className="themed-text-muted shrink-0 text-lg leading-none">›</span>
            </button>
          ))}
        </div>
      </Section>

      <Section
        title={t('settings.platform.overview.byModel', { defaultValue: 'Cost by model' })}
        desc={t('settings.platform.overview.byModelDesc', {
          defaultValue:
            'Which models the money went to. This is the input to deciding whether the credit rate is set correctly.',
        })}
      >
        <div className="themed-bg-secondary rounded-lg overflow-hidden">
          {data.byModel.length === 0 && (
            <div className="px-3 py-4 themed-text-muted text-sm">{t('settings.budget.noData')}</div>
          )}
          {data.byModel.map((m) => (
            <div key={m.model} className="px-3 py-2 border-b themed-border last:border-b-0 flex items-baseline gap-3">
              <span className="themed-text text-sm font-mono truncate flex-1">{m.model}</span>
              <span className="text-[11px] themed-text-muted tabular-nums shrink-0">
                {t('settings.platform.overview.tokens', {
                  defaultValue: '{{in}} in / {{out}} out',
                  in: m.inputTokens.toLocaleString(),
                  out: m.outputTokens.toLocaleString(),
                })}
              </span>
              <span className="themed-text text-sm tabular-nums shrink-0 w-24 text-right">
                {formatMoney(m.costBillingMicro, billingMoney)}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {data.billingEnabled && <MarginSection margin={margin} onOpenSettings={onOpenSettings} />}

      <Section
        title={t('settings.platform.overview.grace', { defaultValue: 'Grace absorbed' })}
        desc={t('settings.platform.overview.graceDesc', {
          defaultValue:
            'Spend the platform paid for on tenants\' behalf after their own budget ran out. This is money leaving your pocket, and the ceiling applies per tenant per period — so the exposure is that figure multiplied by every tenant.',
        })}
      >
        <div className="flex flex-wrap gap-2">
          <StatTile
            label={t('settings.platform.overview.graceThisPeriod', { defaultValue: 'Given away this period' })}
            value={formatMoney(grace.absorbedBillingMicro, billingMoney)}
          />
          <StatTile
            label={t('settings.platform.overview.graceTenants', { defaultValue: 'Tenants that used it' })}
            value={grace.tenants}
          />
          <StatTile
            label={t('settings.platform.overview.graceCap', { defaultValue: 'Ceiling per tenant' })}
            value={grace.capBillingMicroPerTenant === 0
              ? t('settings.platform.overview.graceOff', { defaultValue: 'Off' })
              : formatMoney(grace.capBillingMicroPerTenant, billingMoney)}
            // The hint used to name PAPAN_PLATFORM_GRACE_CAP and promise the
            // control "becomes an editable platform setting in a later phase".
            // It already is one — Platform settings → Grace ceiling — so the
            // tooltip was telling an operator that a control two clicks away did
            // not exist, and pointing at an env var instead.
            hint={t('settings.platform.overview.graceCapHint', {
              defaultValue:
                'Zero turns grace off entirely. Change it on Platform settings → Grace ceiling.',
            })}
            onClick={onOpenSettings}
          />
        </div>
      </Section>
    </div>
  )
}

/**
 * Realised margin (catalogue C3) — the HEADLINE, not the breakdown (F-34).
 *
 * The credit rate IS the gross margin, and it is a number the operator is
 * expected to set — with, before C3, no screen anywhere showing whether the
 * number they set is working.
 *
 * ── Why this is two tiles and not four ──────────────────────────────────
 *
 * The same four figures were rendered here AND on Platform settings, under two
 * different headings ("Realised margin" / "Is that markup working?"). Two
 * copies of one query is a thing that drifts, and neither screen said the
 * other existed — so an operator who found one had no way to know they were
 * looking at a duplicate rather than a second, different number.
 *
 * The BREAKDOWN lives on Platform settings, beside the credit-rate input it
 * argues about: that is the moment the figures change a decision. Overview
 * keeps the one comparison a dashboard is for — realised against set — and a
 * link to the rest. The link matters: without it this is a truncation rather
 * than a division of labour.
 *
 * Stated as a RATE rather than a difference, because the two sides are in
 * different currencies and the only conversion factor in this system is a
 * DISPLAY multiplier that must never be used to reason about money owed.
 * Minor-units-collected per USD-of-provider-cost is directly comparable to
 * `creditRateMinorPerUsd`, which is exactly the comparison worth making.
 */
function MarginSection({ margin, onOpenSettings }: {
  margin: OverviewResponse['margin']
  onOpenSettings?: () => void
}) {
  const { t } = useTranslation('core-ui')
  const cur = margin.currency
  const realised = margin.realisedRateMinorPerUsd

  return (
    <Section
      title={t('settings.platform.overview.margin', { defaultValue: 'Realised margin' })}
      desc={t('settings.platform.overview.marginDesc', {
        defaultValue:
          'What tenants paid this period, against what the providers charged us for the same period.',
      })}
    >
      <div className="flex flex-wrap gap-2">
        <StatTile
          label={t('settings.platform.overview.realisedRate', { defaultValue: 'Realised rate' })}
          value={realised == null
            ? '—'
            : t('settings.platform.overview.perUsd', {
                defaultValue: '{{amount}} / US$1',
                amount: formatCurrencyMinor(Math.round(realised), cur),
              })}
          hint={t('settings.platform.overview.realisedRateHint', {
            defaultValue: 'How much you actually collected per dollar of provider cost.',
          })}
        />
        <StatTile
          label={t('settings.platform.overview.configuredRate', { defaultValue: 'Credit rate (set)' })}
          value={t('settings.platform.overview.perUsd', {
            defaultValue: '{{amount}} / US$1',
            amount: formatCurrencyMinor(margin.creditRateMinorPerUsd, cur),
          })}
          hint={t('settings.platform.overview.configuredRateHint', {
            defaultValue: 'The markup on AI usage, set on the price book. This is the number the realised rate is measured against.',
          })}
          onClick={onOpenSettings}
        />
      </div>

      {/* The caveat is not optional, even in the short form. Prepaid credit is
          bought in one period and spent in another, so a single month's
          realised rate moves with purchase timing rather than with margin —
          reading it as a P&L would be wrong in both directions. */}
      <p className="themed-text-muted text-xs mt-2">
        {t('settings.platform.overview.marginTimingNeutral', {
          defaultValue:
            'Credit bought in one period is often spent in another, so read this across several periods rather than one.',
        })}
        {onOpenSettings && (
          <>
            {' '}
            <button
              onClick={onOpenSettings}
              className="underline underline-offset-2 hover:themed-text transition-colors"
            >
              {t('settings.platform.overview.marginMore', {
                defaultValue: 'See what was collected and what it cost, beside the rate itself.',
              })}
            </button>
          </>
        )}
      </p>

      {margin.excludedInvoices > 0 && (
        <p className="themed-text-muted text-xs mt-1">
          {t('settings.platform.overview.excludedInvoices', {
            defaultValue:
              '{{n}} paid invoice(s) in another currency are not included — they cannot be added to a total labelled {{cur}}.',
            n: margin.excludedInvoices,
            cur: cur.toUpperCase(),
          })}
        </p>
      )}
    </Section>
  )
}

/**
 * The alert strip.
 *
 * Only non-zero counts render. A row of permanent zeros trains the eye to skip
 * the strip, which is the opposite of what an alert is for — the same reason
 * the customer account tab shows its reconciliation line only when it fails.
 *
 * `stuckWebhooks: null` means the count could NOT be read (billing off, or the
 * query failed). It renders as a distinct "not known" chip rather than as zero,
 * because "nothing is stuck" and "we did not look" must never look the same.
 */
function AlertRow({
  alerts, billingEnabled, onOpen,
}: {
  alerts: OverviewResponse['alerts']
  billingEnabled: boolean
  onOpen: () => void
}) {
  const { t } = useTranslation('core-ui')
  const tiles: { key: string; label: string; n: number; tone: 'red' | 'amber' }[] = []

  if (alerts.disputed > 0) {
    tiles.push({ key: 'disputed', n: alerts.disputed, tone: 'red',
      label: t('settings.platform.overview.alert.disputed', { defaultValue: 'disputed' }) })
  }
  if (alerts.pastDue > 0) {
    tiles.push({ key: 'pastDue', n: alerts.pastDue, tone: 'red',
      label: t('settings.platform.overview.alert.pastDue', { defaultValue: 'past due' }) })
  }
  if (alerts.inGrace > 0) {
    tiles.push({ key: 'inGrace', n: alerts.inGrace, tone: 'amber',
      label: t('settings.platform.overview.alert.inGrace', { defaultValue: 'in grace' }) })
  }
  if (alerts.overCap > 0) {
    tiles.push({ key: 'overCap', n: alerts.overCap, tone: 'amber',
      label: t('settings.platform.overview.alert.overCap', { defaultValue: 'over their ceiling' }) })
  }
  if (alerts.stuckWebhooks != null && alerts.stuckWebhooks > 0) {
    tiles.push({ key: 'stuck', n: alerts.stuckWebhooks, tone: 'red',
      label: t('settings.platform.overview.alert.stuckWebhooks', { defaultValue: 'stuck webhooks' }) })
  }

  const unknownWebhooks = billingEnabled && alerts.stuckWebhooks == null

  if (tiles.length === 0 && !unknownWebhooks) {
    return (
      <p className="themed-text-secondary text-sm mb-4">
        {t('settings.platform.overview.allClear', { defaultValue: 'Nothing needs attention.' })}
      </p>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {tiles.map((a) => (
        <button
          key={a.key}
          onClick={onOpen}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs transition-colors ${
            a.tone === 'red'
              ? 'bg-red-500/15 text-red-300 hover:bg-red-500/25'
              : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'
          }`}
        >
          {/* No icon: `@papan/orgs` does not depend on lucide-react, and the
              count plus the tone already carry the alarm. */}
          <span className="tabular-nums font-medium">{a.n}</span>
          <span>{a.label}</span>
          <span aria-hidden>›</span>
        </button>
      ))}
      {unknownWebhooks && (
        <span
          className="px-2.5 py-1.5 rounded text-xs themed-bg-tertiary themed-text-secondary"
          title={t('settings.platform.overview.alert.webhooksUnknownHint', {
            defaultValue: 'The webhook queue could not be read, so this is not a claim that it is empty.',
          })}
        >
          {t('settings.platform.overview.alert.webhooksUnknown', { defaultValue: 'Webhook queue unavailable' })}
        </span>
      )}
    </div>
  )
}
